// BookingWidgetComponent.tsx - Componente de Reserva Integrado
// Widget completo para agendamento de pescarias

import React, { useState, useEffect } from 'react';
import { Calendar, Users, Fish, Phone, Mail, CheckCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

interface BookingWidgetData {
  title?: string;
  subtitle?: string;
  packages: Array<{
    id: string;
    name: string;
    description: string;
    duration: string; // "3 dias", "5 dias"
    price: number;
    pricePerPerson?: number;
    includes: string[];
    maxGuests: number;
    image?: string;
    highlighted?: boolean;
  }>;
  availableDates?: string[]; // Datas disponíveis (ISO)
  blockedDates?: string[]; // Datas bloqueadas (ISO)
  minAdvanceDays?: number; // Mínimo de dias de antecedência
  maxGuests?: number;
  showPrices?: boolean;
  whatsappNumber?: string;
  emailTo?: string;
  successMessage?: string;
  termsUrl?: string;
}

interface Props {
  data: BookingWidgetData;
  settings?: {
    backgroundColor?: string;
    textColor?: string;
  };
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  packageId: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  message: string;
  acceptTerms: boolean;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  packageId: '',
  checkIn: '',
  checkOut: '',
  guests: 2,
  message: '',
  acceptTerms: false,
};

export const BookingWidgetComponent: React.FC<Props> = ({ data, settings }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const {
    title = 'Reserve Sua Pescaria',
    subtitle = 'Escolha o pacote ideal e garanta sua vaga',
    packages = [],
    blockedDates = [],
    minAdvanceDays = 7,
    maxGuests = 10,
    showPrices = true,
    whatsappNumber,
    emailTo,
    successMessage = 'Reserva enviada com sucesso! Entraremos em contato em breve.',
    termsUrl,
  } = data;

  // Gerar dias do mês para o calendário
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: Array<{ date: Date; isCurrentMonth: boolean; isBlocked: boolean; isPast: boolean }> = [];

    // Dias do mês anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date, isCurrentMonth: false, isBlocked: true, isPast: true });
    }

    // Dias do mês atual
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + minAdvanceDays);

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateStr = date.toISOString().split('T')[0];
      const isPast = date < today;
      const isTooSoon = date < minDate;
      const isBlocked = isPast || isTooSoon || blockedDates.includes(dateStr);
      days.push({ date, isCurrentMonth: true, isBlocked, isPast });
    }

    // Dias do próximo mês para completar a grade
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false, isBlocked: true, isPast: false });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Validação do formulário
  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (stepNumber === 1) {
      if (!formData.packageId) {
        newErrors.packageId = 'Selecione um pacote';
      }
    }

    if (stepNumber === 2) {
      if (!formData.checkIn) {
        newErrors.checkIn = 'Selecione a data de entrada';
      }
      if (!formData.guests || formData.guests < 1) {
        newErrors.guests = 'Informe o número de pessoas';
      }
      if (formData.guests > maxGuests) {
        newErrors.guests = `Máximo de ${maxGuests} pessoas`;
      }
    }

    if (stepNumber === 3) {
      if (!formData.name.trim()) {
        newErrors.name = 'Nome é obrigatório';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email é obrigatório';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email inválido';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Telefone é obrigatório';
      }
      if (termsUrl && !formData.acceptTerms) {
        newErrors.acceptTerms = 'Aceite os termos para continuar';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);

    try {
      // Simular envio (substituir por API real)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Se tiver WhatsApp, redirecionar
      if (whatsappNumber) {
        const selectedPackage = packages.find(p => p.id === formData.packageId);
        const message = encodeURIComponent(
          `🎣 *Nova Reserva*\n\n` +
          `*Pacote:* ${selectedPackage?.name}\n` +
          `*Check-in:* ${formData.checkIn}\n` +
          `*Pessoas:* ${formData.guests}\n` +
          `*Nome:* ${formData.name}\n` +
          `*Email:* ${formData.email}\n` +
          `*Telefone:* ${formData.phone}\n` +
          `*Mensagem:* ${formData.message || 'Sem mensagem adicional'}`
        );
        window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
      }

      setIsSuccess(true);
    } catch (error) {
      console.error('Erro ao enviar reserva:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateSelect = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    if (!formData.checkIn) {
      setFormData({ ...formData, checkIn: dateStr });
    } else if (!formData.checkOut && dateStr > formData.checkIn) {
      setFormData({ ...formData, checkOut: dateStr });
    } else {
      setFormData({ ...formData, checkIn: dateStr, checkOut: '' });
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const selectedPackage = packages.find(p => p.id === formData.packageId);

  // Tela de sucesso
  if (isSuccess) {
    return (
      <section 
        className="py-16 px-6"
        style={{ backgroundColor: settings?.backgroundColor || '#f0fdf4' }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Reserva Enviada!</h2>
          <p className="text-lg text-gray-600 mb-8">{successMessage}</p>
          
          <div className="bg-white rounded-xl p-6 shadow-lg text-left">
            <h3 className="font-semibold text-lg mb-4">Resumo da Reserva</h3>
            <div className="space-y-2 text-gray-600">
              <p><strong>Pacote:</strong> {selectedPackage?.name}</p>
              <p><strong>Check-in:</strong> {formatDate(formData.checkIn)}</p>
              <p><strong>Pessoas:</strong> {formData.guests}</p>
              <p><strong>Contato:</strong> {formData.email}</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setIsSuccess(false);
              setFormData(initialFormData);
              setStep(1);
            }}
            className="mt-8 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Fazer Nova Reserva
          </button>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-16 px-6"
      style={{ 
        backgroundColor: settings?.backgroundColor || '#f8fafc',
        color: settings?.textColor 
      }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{title}</h2>
          {subtitle && <p className="text-xl text-gray-600">{subtitle}</p>}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all ${
                step >= stepNum 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {stepNum}
              </div>
              {stepNum < 3 && (
                <div className={`w-20 h-1 mx-2 rounded ${
                  step > stepNum ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Escolher Pacote */}
        {step === 1 && (
          <div className="animate-fadeIn">
            <h3 className="text-2xl font-semibold text-center mb-8">Escolha seu Pacote</h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => setFormData({ ...formData, packageId: pkg.id })}
                  className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-xl ${
                    formData.packageId === pkg.id 
                      ? 'ring-4 ring-blue-500 shadow-xl transform scale-[1.02]' 
                      : 'shadow-md'
                  } ${pkg.highlighted ? 'border-2 border-amber-400' : ''}`}
                >
                  {pkg.highlighted && (
                    <div className="absolute top-4 right-4 bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-semibold">
                      Popular
                    </div>
                  )}
                  
                  {pkg.image && (
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={pkg.image} 
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h4>
                    <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {pkg.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        Até {pkg.maxGuests}
                      </span>
                    </div>

                    {showPrices && (
                      <div className="border-t pt-4">
                        <p className="text-3xl font-bold text-blue-600">
                          R$ {pkg.price.toLocaleString('pt-BR')}
                        </p>
                        {pkg.pricePerPerson && (
                          <p className="text-sm text-gray-500">
                            R$ {pkg.pricePerPerson.toLocaleString('pt-BR')}/pessoa
                          </p>
                        )}
                      </div>
                    )}

                    <ul className="mt-4 space-y-2">
                      {pkg.includes.slice(0, 4).map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {errors.packageId && (
              <p className="text-red-500 text-center mt-4">{errors.packageId}</p>
            )}

            <div className="flex justify-center mt-8">
              <button
                onClick={handleNext}
                disabled={!formData.packageId}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                Continuar
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Escolher Data e Pessoas */}
        {step === 2 && (
          <div className="animate-fadeIn">
            <h3 className="text-2xl font-semibold text-center mb-8">Escolha a Data</h3>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calendário */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                    title="Mês anterior"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h4 className="text-lg font-semibold">
                    {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                  </h4>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                    title="Próximo mês"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center mb-2">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                    <div key={day} className="text-xs font-semibold text-gray-500 py-2">{day}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, i) => {
                    const dateStr = day.date.toISOString().split('T')[0];
                    const isCheckIn = formData.checkIn === dateStr;
                    const isCheckOut = formData.checkOut === dateStr;
                    const isInRange = formData.checkIn && formData.checkOut && 
                      dateStr > formData.checkIn && dateStr < formData.checkOut;

                    return (
                      <button
                        key={i}
                        onClick={() => !day.isBlocked && handleDateSelect(day.date)}
                        disabled={day.isBlocked}
                        className={`p-2 text-sm rounded-lg transition ${
                          !day.isCurrentMonth ? 'text-gray-300' :
                          day.isBlocked ? 'text-gray-300 cursor-not-allowed' :
                          isCheckIn || isCheckOut ? 'bg-blue-600 text-white font-semibold' :
                          isInRange ? 'bg-blue-100 text-blue-800' :
                          'hover:bg-gray-100'
                        }`}
                      >
                        {day.date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-blue-600 rounded" />
                    Selecionado
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-gray-200 rounded" />
                    Indisponível
                  </span>
                </div>
              </div>

              {/* Resumo e Pessoas */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h4 className="font-semibold text-lg mb-4">Pacote Selecionado</h4>
                  <div className="flex items-start gap-4">
                    <Fish className="w-10 h-10 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{selectedPackage?.name}</p>
                      <p className="text-sm text-gray-600">{selectedPackage?.duration}</p>
                      {showPrices && (
                        <p className="text-lg font-bold text-blue-600 mt-2">
                          R$ {selectedPackage?.price.toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h4 className="font-semibold text-lg mb-4">Datas da Viagem</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">Check-in</label>
                      <p className="font-semibold text-lg">
                        {formData.checkIn ? formatDate(formData.checkIn) : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Check-out</label>
                      <p className="font-semibold text-lg">
                        {formData.checkOut ? formatDate(formData.checkOut) : '—'}
                      </p>
                    </div>
                  </div>
                  {errors.checkIn && <p className="text-red-500 text-sm mt-2">{errors.checkIn}</p>}
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h4 className="font-semibold text-lg mb-4">Número de Pessoas</h4>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setFormData({ ...formData, guests: Math.max(1, formData.guests - 1) })}
                      className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold w-12 text-center">{formData.guests}</span>
                    <button
                      onClick={() => setFormData({ ...formData, guests: Math.min(maxGuests, formData.guests + 1) })}
                      className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Máximo {maxGuests} pessoas</p>
                  {errors.guests && <p className="text-red-500 text-sm mt-2">{errors.guests}</p>}
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={handleBack}
                className="px-8 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Voltar
              </button>
              <button
                onClick={handleNext}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition flex items-center gap-2"
              >
                Continuar
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Dados de Contato */}
        {step === 3 && (
          <div className="animate-fadeIn">
            <h3 className="text-2xl font-semibold text-center mb-8">Seus Dados de Contato</h3>
            
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Seu nome completo"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="seu@email.com"
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="(00) 00000-0000"
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem Adicional (opcional)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      placeholder="Alguma observação especial?"
                    />
                  </div>

                  {termsUrl && (
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={formData.acceptTerms}
                        onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                        className="mt-1"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-600">
                        Li e aceito os{' '}
                        <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                          termos e condições
                        </a>
                      </label>
                    </div>
                  )}
                  {errors.acceptTerms && <p className="text-red-500 text-sm">{errors.acceptTerms}</p>}
                </div>

                {/* Resumo Final */}
                <div className="mt-8 pt-6 border-t">
                  <h4 className="font-semibold text-lg mb-4">Resumo da Reserva</h4>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <p><strong>Pacote:</strong> {selectedPackage?.name}</p>
                    <p><strong>Data:</strong> {formatDate(formData.checkIn)} {formData.checkOut && `→ ${formatDate(formData.checkOut)}`}</p>
                    <p><strong>Pessoas:</strong> {formData.guests}</p>
                    {showPrices && selectedPackage && (
                      <p className="text-xl font-bold text-blue-600 pt-2 border-t">
                        Total: R$ {(selectedPackage.price * (selectedPackage.pricePerPerson ? formData.guests : 1)).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={handleBack}
                  className="px-8 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition flex items-center gap-2"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Voltar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 transition flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Confirmar Reserva
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </section>
  );
};
